import {
  Alert,
  Button,
  Card,
  Field,
  PageHeader,
  Skeleton,
  todayInArgentina,
  useToast,
} from '@catering/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Save, Wallet } from 'lucide-react';
import { type JSX, useRef, useState } from 'react';
import { type Resolver, useForm, useWatch } from 'react-hook-form';

import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import { formatPriceCents } from '../../contracts/format-price';
import { searchAllCustomers } from '../../customers/api';
import { registerPayment, type RegisterPaymentInput } from '../../payments/api';
import { mapPaymentError } from '../../payments/payment-api-errors';
import {
  defaultPaymentFormValues,
  paymentFormSchema,
  toRegisterPaymentInput,
  totalApplicableCents,
  type PaymentFormValues,
} from '../../payments/payment-form-schema';
import { listPaymentMethodOptions } from '../../payments/payment-methods-api';
import { listWithholdingTypeOptions } from '../../payments/withholding-types-api';
import { WithholdingLineEditor } from './components/withholding-line-editor';

const paymentNewRoute = getRouteApi('/protected/payments/new');

function maybeError(message: string | undefined): { error: string } | Record<string, never> {
  return message === undefined ? {} : { error: message };
}

/**
 * `payment-receipts/spec.md` → "Registro de un recibo de cobro"/"Pantalla
 * de Cobranzas en el back-office" (UI). Only a create form (no edit route:
 * receipts are immutable once registered — "Inmutabilidad de recibos
 * registrados"). Navigates to `/payments/$id` on success.
 */
export function RegisterPaymentForm(): JSX.Element {
  const currentUser = useCurrentUser();

  if (currentUser.isPending) {
    return <Skeleton className="h-40 w-full" />;
  }

  // The API rejects it anyway; this keeps a direct URL from showing a form
  // that can only fail.
  if (!hasPermission(currentUser.data, 'payment.register')) {
    return (
      <Alert tone="danger" title="Sección restringida">
        No tenés permiso para registrar recibos.
      </Alert>
    );
  }

  return <RegisterPaymentFormContent />;
}

function RegisterPaymentFormContent(): JSX.Element {
  const { customerId: preselectedCustomerId } = paymentNewRoute.useSearch();
  const navigate = paymentNewRoute.useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const customersQuery = useQuery({
    queryKey: ['customers-for-payments'],
    queryFn: () => searchAllCustomers({ q: undefined, zone: undefined, status: undefined }),
  });

  const methodsQuery = useQuery({
    queryKey: ['payment-method-options'],
    queryFn: listPaymentMethodOptions,
  });
  const typesQuery = useQuery({
    queryKey: ['withholding-type-options'],
    queryFn: listWithholdingTypeOptions,
  });

  // Generated once per form instance, not per submit: a double submit
  // (double click, retry after a dropped response) reuses the same key so
  // the API's idempotent replay absorbs it instead of creating a second
  // receipt (`payment-receipts/spec.md` → "Idempotencia ante reintento de red").
  const idempotencyKeyRef = useRef(crypto.randomUUID());
  const [today] = useState(() => todayInArgentina());

  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<PaymentFormValues, unknown, PaymentFormValues>({
    resolver: zodResolver(paymentFormSchema) as unknown as Resolver<
      PaymentFormValues,
      unknown,
      PaymentFormValues
    >,
    defaultValues: {
      ...defaultPaymentFormValues(today),
      ...(preselectedCustomerId === undefined ? {} : { customerId: preselectedCustomerId }),
    },
  });

  const amountPesos = useWatch({ control, name: 'amountPesos' });
  const withholdings = useWatch({ control, name: 'withholdings' });

  const mutation = useMutation({
    mutationFn: (input: RegisterPaymentInput) => registerPayment(input),
    onSuccess: async (payment) => {
      await queryClient.invalidateQueries({ queryKey: ['payments'] });
      toast('success', 'Recibo registrado');
      await navigate({ to: '/payments/$id', params: { id: payment.id } });
    },
  });

  const formError = mapPaymentError(mutation.error);

  async function handleValidSubmit(values: PaymentFormValues): Promise<void> {
    await mutation
      .mutateAsync(toRegisterPaymentInput(values, idempotencyKeyRef.current))
      .catch(() => undefined);
  }

  // Without this, a failed options query leaves the form on "Cargando..." forever.
  const loadFailed = customersQuery.isError || methodsQuery.isError || typesQuery.isError;

  function retryLoad(): void {
    for (const query of [customersQuery, methodsQuery, typesQuery]) {
      if (query.isError) {
        void query.refetch();
      }
    }
  }

  const total = totalApplicableCents({
    amountPesos: amountPesos ?? '',
    withholdings: withholdings ?? [],
  });

  return (
    <section className="flex max-w-2xl flex-col gap-6">
      <PageHeader
        icon={Wallet}
        title="Registrar recibo"
        description="Cargá un recibo de cobro para imputarlo a facturas más adelante."
      />
      <Card>
        {loadFailed ? (
          <Alert tone="danger" title="No pudimos cargar los datos del formulario">
            <div className="flex items-center justify-between gap-4">
              <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
              <Button variant="secondary" size="sm" onClick={retryLoad}>
                Reintentar
              </Button>
            </div>
          </Alert>
        ) : customersQuery.data === undefined ||
          methodsQuery.data === undefined ||
          typesQuery.data === undefined ? (
          <p className="text-sm text-slate-500">Cargando...</p>
        ) : (
          <form
            onSubmit={(event) => void handleSubmit(handleValidSubmit)(event)}
            className="flex flex-col gap-6"
            noValidate
          >
            {formError !== '' ? <Alert tone="danger">{formError}</Alert> : null}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="payment-customer" className="text-sm font-medium text-slate-900">
                  Cliente
                  <span aria-hidden="true" className="ml-0.5 text-red-600">
                    *
                  </span>
                </label>
                <select
                  id="payment-customer"
                  className="h-11 rounded-md border border-slate-300 px-3 text-sm"
                  {...register('customerId')}
                >
                  <option value="">Seleccioná un cliente</option>
                  {customersQuery.data.items.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.legalName}
                    </option>
                  ))}
                </select>
                {errors.customerId?.message === undefined ? null : (
                  <p role="alert" className="text-xs text-red-600">
                    {errors.customerId.message}
                  </p>
                )}
              </div>

              <Field
                label="Fecha de pago"
                type="date"
                required
                {...register('paymentDate')}
                {...maybeError(errors.paymentDate?.message)}
              />

              <Field
                label="N° de operación, cheque o transferencia"
                required
                {...register('referenceNumber')}
                {...maybeError(errors.referenceNumber?.message)}
              />

              <div className="flex flex-col gap-1.5">
                <label htmlFor="payment-method" className="text-sm font-medium text-slate-900">
                  Método
                  <span aria-hidden="true" className="ml-0.5 text-red-600">
                    *
                  </span>
                </label>
                <select
                  id="payment-method"
                  className="h-11 rounded-md border border-slate-300 px-3 text-sm"
                  {...register('methodId')}
                >
                  <option value="">Seleccioná un método</option>
                  {methodsQuery.data.map((method) => (
                    <option key={method.id} value={method.id}>
                      {method.name}
                    </option>
                  ))}
                </select>
                {errors.methodId?.message === undefined ? null : (
                  <p role="alert" className="text-xs text-red-600">
                    {errors.methodId.message}
                  </p>
                )}
              </div>

              <Field
                label="Monto recibido"
                inputMode="decimal"
                required
                {...register('amountPesos')}
                {...maybeError(errors.amountPesos?.message)}
              />
            </div>

            <Field label="Notas" {...register('notes')} {...maybeError(errors.notes?.message)} />

            <div className="flex flex-col gap-3">
              <h2 className="text-sm font-semibold text-slate-900">Retenciones y percepciones</h2>
              <p className="text-xs text-slate-500">
                Solo si el cliente te descontó impuestos al pagar: cargá una línea por cada
                retención, con el número de su certificado.
              </p>
              <WithholdingLineEditor
                control={control}
                register={register}
                getValues={getValues}
                setValue={setValue}
                typeOptions={typesQuery.data}
                errors={errors.withholdings}
              />
            </div>

            <dl className="flex items-center justify-between border-t border-slate-200 pt-4 text-sm">
              <dt className="font-medium text-slate-900">Total aplicable</dt>
              <dd className="font-semibold text-slate-900">{formatPriceCents(total)}</dd>
            </dl>

            <div className="flex items-center justify-end gap-3">
              <Button type="submit" icon={Save} loading={mutation.isPending}>
                Registrar recibo
              </Button>
            </div>
          </form>
        )}
      </Card>
    </section>
  );
}
